import { NetworkErrorCode, WsNetworkError } from '../wsErrors/WsNetworkError';
import { Listener, Stream } from './Stream';
import { WebSocketService, WsDisconnectCode, WsEventType } from './WebsocketService';

export enum NetworkEventType {
  Message = 'message',
  WsError = 'ws-error',
  Close = 'close',
  Open = 'open',
  PingPongError = 'ping-pong-error',
  NetworkError = 'error',
  Retry = 'retry',
}

type NetworkEventMap = {
  [NetworkEventType.Message]: unknown;
  [NetworkEventType.WsError]: Event;
  [NetworkEventType.Close]: CloseEvent;
  [NetworkEventType.Open]: Event;
  [NetworkEventType.Retry]: unknown;
  [NetworkEventType.NetworkError]: WsNetworkError;
  [NetworkEventType.PingPongError]: WsNetworkError;
};

type StreamMap<T extends NetworkEventType> = Record<T, Stream<NetworkEventMap[T]>>;

const MAX_PING_PONG_INTERVAL = 10 * 1000;

export class WsNetworkService {
  ws: WebSocketService;
  withoutPingPong = false;
  private timeout: ReturnType<typeof setTimeout>;

  streams: StreamMap<NetworkEventType> = {
    [NetworkEventType.Message]: new Stream<unknown>(),
    [NetworkEventType.WsError]: new Stream<Event>(),
    [NetworkEventType.Close]: new Stream<CloseEvent>(),
    [NetworkEventType.Open]: new Stream<Event>(),
    [NetworkEventType.Retry]: new Stream<unknown>(),
    [NetworkEventType.NetworkError]: new Stream<WsNetworkError>(),
    [NetworkEventType.PingPongError]: new Stream<WsNetworkError>(),
  };

  private unsubscribes: Record<WsEventType, (() => void) | null> = {
    [WsEventType.Message]: null,
    [WsEventType.Error]: null,
    [WsEventType.Close]: null,
    [WsEventType.Open]: null,
  };

  constructor() {
    this.ws = new WebSocketService();
    this.unsubscribes[WsEventType.Message] = this.ws.on(
      WsEventType.Message,
      (event: MessageEvent) => this.handleWsMessage(event)
    );

    this.unsubscribes[WsEventType.Close] = this.ws.on(WsEventType.Close, (event: CloseEvent) =>
      this.handleWsClose(event)
    );

    this.unsubscribes[WsEventType.Error] = this.ws.on(WsEventType.Error, (event: Event) =>
      this.handleWsError(event)
    );

    this.unsubscribes[WsEventType.Open] = this.ws.on(WsEventType.Open, (event: Event) =>
      this.handleWsOpen(event)
    );
  }

  public on<T extends NetworkEventType>(
    type: T,
    listener: Listener<NetworkEventMap[T]>
  ): () => void {
    return this.streams[type].on(listener);
  }

  public off<T extends NetworkEventType>(type: T, listener: Listener<NetworkEventMap[T]>): void {
    this.streams[type].off(listener);
  }

  public error(error: WsNetworkError): void {
    this.streams[NetworkEventType.NetworkError].emit(error);
  }

  public retry(): void {
    this.streams[NetworkEventType.Retry].emit({});
  }

  public connect(url: string, binaryType: BinaryType = 'blob', withoutPingPong?: boolean): void {
    this.withoutPingPong = !!withoutPingPong;
    this.ws.connect(url, binaryType);
  }

  public disconnect(code?: WsDisconnectCode): void {
    this.ws.disconnect(code);
  }

  public destroy(): void {
    Object.keys(this.unsubscribes).forEach((evenType: WsEventType) => {
      this.unsubscribes[evenType]?.();
      this.unsubscribes[evenType] = null;
    });
  }

  public send(message: string | ArrayBufferView): void {
    this.ws.send(message);
  }

  public async pingPongRequest(): Promise<void> {
    return;
  }

  public async pingPong(): Promise<void> {
    let success = false;

    this.pingPongRequest().then(() => {
      success = true;
    });

    this.timeout = setTimeout(() => {
      if (success) {
        this.pingPong();
      } else {
        this.streams[NetworkEventType.PingPongError].emit(
          new WsNetworkError(NetworkErrorCode.ServiceUnavailable, 'Server close channel')
        );
      }
    }, MAX_PING_PONG_INTERVAL);
  }

  public get isWsOpen(): boolean {
    return this.ws.readyState === WebSocket.OPEN;
  }

  private handleWsMessage(message: MessageEvent): void {
    this.streams[NetworkEventType.Message].emit(message);
  }

  private handleWsClose(event: CloseEvent): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.streams[NetworkEventType.Close].emit(event);
  }

  private handleWsError(event: Event): void {
    this.streams[NetworkEventType.WsError].emit(event);
  }

  private handleWsOpen(event: Event): void {
    this.streams[NetworkEventType.Open].emit(event);
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (!this.withoutPingPong) {
      this.pingPong();
    }
  }
}
